/**
 * The camera as a still photograph: the live tile at the head of the attach
 * menu, and the frame taken out of it.
 *
 * Separate from capture.ts, which records: a кружочек is the centre square of
 * the camera drawn into a small canvas and encoded frame by frame, and a
 * photograph is one full frame of whatever the camera actually sees, at the
 * size it sees it. The one thing the two share is which way the camera points,
 * so the facing preference stays over there and is read from here — pointing it
 * at yourself for a кружочек and finding it pointed at the wall for a photo
 * would be the same camera disagreeing with itself.
 *
 * Nothing here compresses: an upload is re-encoded on the server anyway (see
 * server/src/images.ts), and unlike a recording there is no real-time playback
 * problem to work around.
 */

import { type Facing } from './capture.ts';
import { type Tilt } from './tilt.ts';

/**
 * What the camera is asked for. A photograph is not cropped to anything here,
 * so this is a request for detail rather than for a shape — the server resizes
 * to IMAGE_MAX_WIDTH regardless, and asking for more than that would only make
 * the frame slower to grab.
 */
const IDEAL_WIDTH = 1920;
const IDEAL_HEIGHT = 1080;

/** How hard the grabbed frame is squeezed on the way to the album tile. */
const QUALITY = 0.92;

/**
 * Whether the camera is already ours to open.
 *
 * The attach menu is opened to send a file just as often as to take a picture,
 * and a permission prompt fired by opening a menu is a prompt nobody asked for.
 * So the viewfinder starts by itself only once permission is already granted,
 * and otherwise waits for a tap on the tile.
 *
 * Anything but a definite «granted» is treated as «ask»: Safari has no camera
 * entry in the Permissions API at all and rejects the query outright, which
 * must mean one extra tap rather than no camera.
 */
export async function cameraGranted(): Promise<boolean> {
  try {
    const status = await navigator.permissions?.query({ name: 'camera' as PermissionName });
    return status?.state === 'granted';
  } catch {
    return false;
  }
}

export function openViewfinder(facing: Facing): Promise<MediaStream> {
  return navigator.mediaDevices.getUserMedia({
    video: { facingMode: facing, width: { ideal: IDEAL_WIDTH }, height: { ideal: IDEAL_HEIGHT } },
    audio: false,
  });
}

/**
 * Whether there is a second camera to flip to. A phone has two; a laptop
 * usually has one, and a flip button over a webcam that cannot flip is a
 * control that does nothing.
 */
export async function hasSecondCamera(): Promise<boolean> {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices.filter((device) => device.kind === 'videoinput').length > 1;
  } catch {
    return false;
  }
}

/**
 * `photo-20260806-142530.jpg` — a name that sorts by when it was taken.
 *
 * Only ever a hint: the server names what it stores. It is what the album tile
 * and the file bubble show, though, so «photo-...» beats «blob».
 */
export function photoFileName(at: Date = new Date()): string {
  const pad = (value: number) => String(value).padStart(2, '0');
  const date = `${at.getFullYear()}${pad(at.getMonth() + 1)}${pad(at.getDate())}`;
  const time = `${pad(at.getHours())}${pad(at.getMinutes())}${pad(at.getSeconds())}`;
  return `photo-${date}-${time}.jpg`;
}

/**
 * The frame currently on screen, as a file the album can hold.
 *
 * Drawn straight from the video element, which means the picture is *not*
 * mirrored even when the preview is. A self-view is flipped so that framing
 * works the way a mirror does — everyone expects to lean left and see the
 * picture lean left — but a photograph that came out flipped would have any
 * writing in it backwards, and every phone camera saves the unflipped frame for
 * exactly that reason. The mirror is in the CSS, and stays there.
 *
 * `rotation` is the quarter-turn, clockwise, that puts the frame the right way
 * up — see tilt.ts. The frame itself is always portrait, because the page is:
 * a phone turned on its side hands the page the same portrait frames with the
 * scene lying in them, and the turn has to happen here, into a canvas of the
 * other shape, or the photograph arrives lying on its side too. Turned in the
 * pixels rather than noted in EXIF, which `toBlob` cannot write anyway — and
 * the album tile shows the file as it is, before the server has had a look at
 * any tag.
 */
export async function snapshot(video: HTMLVideoElement, rotation: Tilt = 0): Promise<File> {
  const width = video.videoWidth;
  const height = video.videoHeight;
  // A camera that is open but has not delivered a frame yet has no size, and
  // drawing that gives a blank picture rather than an error.
  if (width === 0 || height === 0) throw new Error('Кадр ещё не готов');

  const sideways = rotation === 90 || rotation === 270;
  const canvas = document.createElement('canvas');
  canvas.width = sideways ? height : width;
  canvas.height = sideways ? width : height;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Не удалось подготовить кадр');
  // About the canvas's centre, so the turned frame lands inside it whichever
  // shape the canvas took. A canvas's positive angle is clockwise — its y axis
  // points down — which is the direction rotationFor speaks in.
  context.translate(canvas.width / 2, canvas.height / 2);
  context.rotate((rotation * Math.PI) / 180);
  context.drawImage(video, -width / 2, -height / 2);

  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, 'image/jpeg', QUALITY);
  });
  if (!blob) throw new Error('Не удалось снять кадр');

  return new File([blob], photoFileName(), { type: 'image/jpeg' });
}
